// frontend/src/components/contacts/ContactForm.jsx
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { formatPhone } from "../../utils/formatPhone";
import { toast } from "react-toastify";

const ContactForm = ({ contact, onSave, onCancel }) => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    contact_title: "",
    phone: "",
    email: "",
    website: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    industry: "",
    notes: "",
    relationship_status: "prospect",
    last_contact_date: "",
    branch_id: "",
    is_active: true,
  });

  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load states for dropdown
  useEffect(() => {
    const loadStates = async () => {
      try {
        const res = await secureAxios.get(`${apiBase}/api/states`);
        setStates(res.data);
      } catch (err) {
        console.error("Error loading states:", err);
      }
    };
    loadStates();
  }, [apiBase]);

  // Load branches for dropdown
  useEffect(() => {
    const loadBranches = async () => {
      if (!user?.tenant_id) return;
      try {
        const res = await secureAxios.get(`${apiBase}/api/branches`, {
          params: { tenant_id: user.tenant_id, is_active: true },
        });
        setBranches(res.data);
      } catch (err) {
        console.error("Error loading branches:", err);
      }
    };
    loadBranches();
  }, [apiBase, user?.tenant_id]);

  // Populate form when editing
  useEffect(() => {
    if (contact) {
      setFormData({
        company_name: contact.company_name || "",
        contact_name: contact.contact_name || "",
        contact_title: contact.contact_title || "",
        phone: formatPhone(contact.phone || ""),
        email: contact.email || "",
        website: contact.website || "",
        address_line_1: contact.address_line_1 || "",
        address_line_2: contact.address_line_2 || "",
        city: contact.city || "",
        state_id: contact.state_id || "",
        postal_code: contact.postal_code || "",
        industry: contact.industry || "",
        notes: contact.notes || "",
        relationship_status: contact.contact_status || contact.relationship_status || "prospect",
        last_contact_date: contact.last_contact_date
          ? contact.last_contact_date.split("T")[0]
          : "",
        branch_id: contact.branch_id || "",
        is_active: contact.is_active !== false,
      });
    }
  }, [contact]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;

    if (name === "phone") {
      newValue = formatPhone(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : newValue,
    }));

    if (error) setError("");
  };

  const validateForm = () => {
    if (!formData.company_name.trim()) {
      setError("Company name is required");
      return false;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError("");

    const payload = {
      ...formData,
      phone: formData.phone.replace(/\D/g, ""),
      tenant_id: user.tenant_id,
      last_contact_date: formData.last_contact_date || null,
      branch_id: formData.branch_id || null,
      contact_status: formData.relationship_status,
      [contact ? "updated_by" : "created_by"]: user.user_id,
    };

    try {
      if (contact) {
        await secureAxios.put(
          `${apiBase}/api/employers/${contact.employer_id}`,
          payload
        );
        toast.success("Contact updated successfully");
      } else {
        await secureAxios.post(`${apiBase}/api/employers`, payload);
        toast.success("Contact created successfully");
      }

      onSave();
    } catch (err) {
      console.error("Error saving contact:", err);
      setError(err.response?.data?.error || "Failed to save contact");
    } finally {
      setLoading(false);
    }
  };

  // Industry options
  const industries = [
    "Agriculture",
    "Construction",
    "Education",
    "Finance",
    "Food Service",
    "Healthcare",
    "Hospitality",
    "Information Technology",
    "Manufacturing",
    "Non-Profit",
    "Retail",
    "Transportation",
    "Utilities",
    "Warehouse/Logistics",
    "Other",
  ];

  // Relationship status options
  const relationshipStatuses = [
    { value: "prospect", label: "Prospect" },
    { value: "contacted", label: "Contacted" },
    { value: "interested", label: "Interested" },
    { value: "active-partner", label: "Active Partner" },
    { value: "inactive", label: "Inactive" },
  ];

  return (
    <div className="container my-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 mb-md-3 gap-2">
        <h5 className="mb-0 h6">
          <i className="bi bi-building me-2"></i>
          {contact ? "Edit Contact" : "Add New Contact"}
        </h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          <i className="bi bi-arrow-left me-1"></i>
          <span className="d-none d-sm-inline">Back to </span>List
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible py-2 small">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card mb-2 mb-md-3">
          <div className="card-header bg-primary text-white py-2">
            <i className="bi bi-info-circle me-2"></i>
            Company Information
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="row g-2 g-md-3">
              <div className="col-12 col-md-6">
                <label className="form-label small">
                  Company Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  className="form-control form-control-sm"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label small">Industry</label>
                <select
                  name="industry"
                  className="form-select form-select-sm"
                  value={formData.industry}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select</option>
                  {industries.map((ind) => (
                    <option key={ind} value={ind}>
                      {ind}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label small">Website</label>
                <input
                  type="text"
                  name="website"
                  className="form-control form-control-sm"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="www.example.com"
                  disabled={loading}
                />
              </div>

              {/* Branch/Office Dropdown */}
              <div className="col-12 col-md-6">
                <label className="form-label small">
                  Branch/Office
                  <i
                    className="bi bi-info-circle ms-1 text-muted"
                    title="Organize contacts by branch or office location"
                    style={{ cursor: "help" }}
                  ></i>
                </label>
                <select
                  name="branch_id"
                  className="form-select form-select-sm"
                  value={formData.branch_id}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- No Branch/Office --</option>
                  {branches.map((branch) => (
                    <option key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch_name}
                    </option>
                  ))}
                </select>
                <div className="form-text small">
                  Optional: Group this contact under a specific branch or office
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-2 mb-md-3">
          <div className="card-header bg-success text-white py-2">
            <i className="bi bi-person me-2"></i>
            Contact Person
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="row g-2 g-md-3">
              <div className="col-6 col-sm-6 col-md-4">
                <label className="form-label small">Contact Name</label>
                <input
                  type="text"
                  name="contact_name"
                  className="form-control form-control-sm"
                  value={formData.contact_name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  disabled={loading}
                />
              </div>

              <div className="col-6 col-sm-6 col-md-3">
                <label className="form-label small">Title</label>
                <input
                  type="text"
                  name="contact_title"
                  className="form-control form-control-sm"
                  value={formData.contact_title}
                  onChange={handleChange}
                  placeholder="HR Manager"
                  disabled={loading}
                />
              </div>

              <div className="col-6 col-sm-6 col-md-2">
                <label className="form-label small">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-control form-control-sm"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(xxx) xxx-xxxx"
                  disabled={loading}
                />
              </div>

              <div className="col-12 col-sm-6 col-md-3">
                <label className="form-label small">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control form-control-sm"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-2 mb-md-3">
          <div className="card-header bg-secondary text-white py-2">
            <i className="bi bi-geo-alt me-2"></i>
            Address
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="row g-2 g-md-3">
              <div className="col-12 col-md-6">
                <label className="form-label small">Address Line 1</label>
                <input
                  type="text"
                  name="address_line_1"
                  className="form-control form-control-sm"
                  value={formData.address_line_1}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label small">Address Line 2</label>
                <input
                  type="text"
                  name="address_line_2"
                  className="form-control form-control-sm"
                  value={formData.address_line_2}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="col-6 col-md-4">
                <label className="form-label small">City</label>
                <input
                  type="text"
                  name="city"
                  className="form-control form-control-sm"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="col-6 col-md-4">
                <label className="form-label small">State</label>
                <select
                  name="state_id"
                  className="form-select form-select-sm"
                  value={formData.state_id}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select</option>
                  {states.map((state) => (
                    <option key={state.state_id} value={state.state_id}>
                      {state.state_code} - {state.state_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4">
                <label className="form-label small">ZIP Code</label>
                <input
                  type="text"
                  name="postal_code"
                  className="form-control form-control-sm"
                  value={formData.postal_code}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-2 mb-md-3">
          <div className="card-header bg-info text-white py-2">
            <i className="bi bi-graph-up me-2"></i>
            Relationship Tracking
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="row g-2 g-md-3">
              <div className="col-6 col-md-4">
                <label className="form-label small">Relationship Status</label>
                <select
                  name="relationship_status"
                  className="form-select form-select-sm"
                  value={formData.relationship_status}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {relationshipStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4">
                <label className="form-label small">Last Contact Date</label>
                <input
                  type="date"
                  name="last_contact_date"
                  className="form-control form-control-sm"
                  value={formData.last_contact_date}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-2 mb-md-3">
          <div className="card-header bg-secondary text-white py-2">
            <i className="bi bi-journal-text me-2"></i>
            Additional Information
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="row g-2 g-md-3">
              <div className="col-12">
                <label className="form-label small">Notes</label>
                <textarea
                  name="notes"
                  className="form-control form-control-sm"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Any additional notes..."
                  disabled={loading}
                />
              </div>

              {contact && (
                <div className="col-12">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      name="is_active"
                      className="form-check-input"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <label className="form-check-label small" htmlFor="is_active">
                      Active Contact
                    </label>
                    <small className="text-muted d-block">
                      Inactive contacts won't appear in dropdown selections
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="d-flex flex-column flex-sm-row gap-2 mt-3 mt-md-4">
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-1"></i>
                {contact ? "Update" : "Create"}<span className="d-none d-sm-inline"> Contact</span>
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;